/**
 * Currencies ACME pays in.
 *
 * `exponent` is the number of decimal places the currency subdivides into — 2
 * for USD (cents), 0 for JPY (no subunit). Every conversion between major and
 * minor units reads this, so no code anywhere assumes "money means cents".
 */
export interface CurrencyDefinition {
  readonly code: string
  readonly name: string
  readonly exponent: number
}

export const CURRENCIES = {
  USD: { code: 'USD', name: 'US Dollar', exponent: 2 },
  EUR: { code: 'EUR', name: 'Euro', exponent: 2 },
  GBP: { code: 'GBP', name: 'Pound Sterling', exponent: 2 },
  INR: { code: 'INR', name: 'Indian Rupee', exponent: 2 },
  SGD: { code: 'SGD', name: 'Singapore Dollar', exponent: 2 },
  AED: { code: 'AED', name: 'UAE Dirham', exponent: 2 },
  AUD: { code: 'AUD', name: 'Australian Dollar', exponent: 2 },
  CAD: { code: 'CAD', name: 'Canadian Dollar', exponent: 2 },
  PLN: { code: 'PLN', name: 'Polish Zloty', exponent: 2 },
  BRL: { code: 'BRL', name: 'Brazilian Real', exponent: 2 },
  ZAR: { code: 'ZAR', name: 'South African Rand', exponent: 2 },
  JPY: { code: 'JPY', name: 'Japanese Yen', exponent: 0 },
} as const satisfies Record<string, CurrencyDefinition>

export type CurrencyCode = keyof typeof CURRENCIES

export const CURRENCY_CODES = Object.keys(CURRENCIES) as readonly CurrencyCode[]

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === 'string' && value in CURRENCIES
}

export function minorUnitExponent(currency: CurrencyCode): number {
  return CURRENCIES[currency].exponent
}
